import { useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { NavigateFunction } from 'react-router';

import type { AppIconName } from '@/config/appIcons';
import {
  BaseUrlRoute,
  DiagnosticsRoute,
  LanguageRoute,
  PersonsRoute,
} from '@/config/router/routes';
import { appRepository } from '@/data/appRepositoryImpl';
import type { L10n } from '@/l10n/types';
import { useL10n } from '@/l10n/useL10n';
import { BackgroundWrapper } from '@/presentation/widgets/BackgroundWrapper';
import { EasyLoading } from '@/presentation/widgets/loading/loadingOverlay';
import { PrimaryIconButtonRounded } from '@/presentation/widgets/PrimaryIconButton';
import { useTextController } from '@/presentation/widgets/PrimaryInputField';
import type { TextController } from '@/presentation/widgets/PrimaryInputField';
import { PrimaryPinputField } from '@/presentation/widgets/PrimaryPinputField';
import { SafeArea } from '@/presentation/widgets/SafeArea';
import {
  SnackbarManager,
  SnackbarType,
} from '@/presentation/widgets/snackbar/snackbarManager';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import { stackTraceOf } from '@/utils/exceptions/appException';
import {
  AppFailure,
  FailureOp,
  failureKindIsNetwork,
} from '@/utils/exceptions/appFailure';
import { wv } from '@/utils/screenUtil';
import { StorageHelper } from '@/utils/storageHelper';

import { DigitKeyboard } from './digitKeyboard/DigitKeyboard';
import { VerifyAdminType, verifyAdminTypeFromParam } from './verifyAdminType';
import styles from './VerifyAdminPage.module.css';

/**
 * Перенос `lib/src/presentation/pages/verify_admin/verify_admin_page.dart`.
 *
 * Из Android-части убрано: снятие режима киоска и системного лаунчера,
 * закрытие приложения (`SystemNavigator.pop`) и отладочный просмотр папки
 * с apk (`system_files_viewer` + `path_provider`). Что заняло место
 * выхода — см. комментарий в [proceed].
 *
 * `Scaffold` не переносится: в `main.dart` у него прозрачный фон
 * (`scaffoldBackgroundColor: Colors.transparent`), ни AppBar, ни кнопок
 * на нём нет, а сам фон рисует `AppBackground` под маршрутизатором.
 *
 * Проверка кода вынесена из компонента в обычные функции: в Dart это были
 * методы `State`, которым нужны те же четыре вещи — контроллер поля,
 * навигация, переводы и признак «экран ещё жив».
 */

/** `Duration(seconds: 6)` у отказа без сети. */
const OFFLINE_REFUSAL_DURATION_MS = 6000;

/** `{VerifyAdminType.changePerson, VerifyAdminType.exitApp}` в переключателе. */
const SWITCHER_TYPES: readonly VerifyAdminType[] = [
  VerifyAdminType.changePerson,
  VerifyAdminType.exitApp,
];

/**
 * Двери, которые открывает сохранённый код, когда сервера нет.
 *
 * `exitApp` был таким и в Dart: иначе киоск заперт до появления сети.
 * `changeServer` добавлен по той же причине, только острее — мёртвый сервер
 * и есть повод менять адрес, а проверить код у него в этот момент негде.
 * Дверь, закрытую ровно тогда, когда она нужна, можно было бы и не делать.
 *
 * Смены аватара здесь нет и не было: список всё равно приходит с сервера.
 */
const OFFLINE_DOORS: readonly VerifyAdminType[] = [
  VerifyAdminType.exitApp,
  VerifyAdminType.changeServer,
];

/** `DigitKeyboard(digitSize: 54.w, spacing: 10.w)` */
const DIGIT_SIZE = 54;
const DIGIT_SPACING = 10;

/** `int.tryParse(value)`: не целое число — `null`, а не `NaN`. */
const INTEGER_PATTERN = /^-?\d+$/;

function tryParseInt(value: string): number | null {
  return INTEGER_PATTERN.test(value) ? Number(value) : null;
}

function switcherIcon(type: VerifyAdminType): AppIconName {
  switch (type) {
    case VerifyAdminType.changePerson:
      return 'lock';
    case VerifyAdminType.exitApp:
      return 'exit';
    // В переключателе не показываются.
    case VerifyAdminType.diagnostics:
      return 'settings';
    case VerifyAdminType.changeServer:
      return 'compas';
  }
}

/**
 * `dev.log(failure.diagnostics, name: 'Failure', error:, stackTrace:)`.
 *
 * Отдельного канала логов в браузере нет, поэтому та же строка уходит в
 * `console.debug` — так же, как у `SnackbarManager`.
 */
function logFailure(failure: AppFailure): void {
  const details = [failure.cause, failure.stackTrace].filter(
    (part) => part !== null && part !== undefined,
  );
  console.debug(`[Failure] ${failure.diagnostics}`, ...details);
}

/**
 * Сбой связи, а не отказ сервера: только в этом случае имеет смысл
 * сверяться с сохранённым кодом.
 */
function isNetworkFailure(failure: AppFailure): boolean {
  // `_isNetworkFailure` со страницы слово в слово повторял набор причин,
  // который уже описан в `AppFailure`.
  return failureKindIsNetwork(failure.kind);
}

/** Всё, что методам `State` давал сам `State`. */
interface PinCheckContext {
  readonly controller: TextController;
  readonly navigate: NavigateFunction;
  readonly l10n: L10n;
  /** `mounted` — экран ещё в дереве. */
  readonly mounted: RefObject<boolean>;
}

/**
 * Куда ведёт дверь, открывающая экран адреса.
 *
 * Тип в адресе — не тот, которым дверь открыли: экран адреса после удачного
 * подключения уходит обратно на PIN с тем же типом ([useBaseUrlController]).
 * Дай ему `changeServer` или `exitApp` — и получится круг: адрес → PIN →
 * адрес. `changePerson` его размыкает: администратор подтверждает код ещё
 * раз и попадает к списку аватаров.
 */
function goToBaseUrl(navigate: NavigateFunction): void {
  BaseUrlRoute.go(navigate, VerifyAdminType.changePerson);
}

/** `_proceed` */
async function proceed(context: PinCheckContext, type: VerifyAdminType): Promise<void> {
  const { navigate, mounted } = context;
  switch (type) {
    case VerifyAdminType.changePerson:
      PersonsRoute.go(navigate);
      return;
    case VerifyAdminType.diagnostics:
      DiagnosticsRoute.go(navigate);
      return;
    case VerifyAdminType.changeServer:
      goToBaseUrl(navigate);
      return;
    case VerifyAdminType.exitApp:
      // Выход по PIN доказывает, что это администратор: при следующем
      // запуске спросим адрес сервера. Ставим до выхода — после pop
      // процесс может завершиться раньше, чем дойдёт до записи.
      await StorageHelper.instance.setAskBaseUrlAfterExit(true);
      if (!mounted.current) return;
      // Дальше Dart снимал режим киоска, возвращал системный лаунчер и
      // закрывал приложение. В вебе выхода нет: вкладка не может закрыть
      // себя сама. Флаг выше остаётся — он и приводит на экран адреса при
      // следующем запуске, — поэтому туда же уходим сразу. Это
      // единственное место, где отказ от Android-части виден человеку.
      goToBaseUrl(navigate);
      return;
    default: {
      // Режим без своей ветки открыл бы дверь в никуда: `proceed` ничего
      // не вернёт, и на верный код экран просто замрёт. `never` не даст
      // завести новый режим, не дописав сюда.
      const unhandled: never = type;
      throw new Error(`verify-admin: режим без перехода — ${String(unhandled)}`);
    }
  }
}

/** Отказ без сети с объяснением вместо общего «нет сети». */
function showOfflineRefusal(
  context: PinCheckContext,
  failure: AppFailure,
  message: (l10n: L10n) => string,
): void {
  const { controller, l10n, mounted } = context;
  if (!mounted.current) return;
  controller.clear();
  logFailure(failure);
  const title = message(l10n);
  DiagnosticsLog.instance.failure(failure, { message: title });
  SnackbarManager.instance.show({
    type: SnackbarType.error,
    title,
    code: failure.code,
    autoCloseDuration: OFFLINE_REFUSAL_DURATION_MS,
    logToast: false,
  });
}

/** Тело `catch (e, stk)` из `checkPincode`: вся офлайн-лестница. */
async function handleFailure(
  context: PinCheckContext,
  type: VerifyAdminType,
  pincode: number,
  error: unknown,
): Promise<void> {
  const { controller, l10n, mounted } = context;
  const failure = AppFailure.fromException(FailureOp.pin, error, stackTraceOf(error));

  // Сеть недоступна, а человек хочет выйти из приложения или сменить адрес
  // сервера. Сверяем код с последним, который принял сервер: иначе киоск
  // заперт до появления сети. Для смены аватара офлайна нет — список всё
  // равно с сервера.
  const storage = StorageHelper.instance;
  const network = isNetworkFailure(failure);
  const saved = storage.pinHash !== null;
  // Хеш в вебе считает `crypto.subtle`, поэтому сверка асинхронная. Бросить
  // она не может: без сохранённого хеша код даже не считается, а сохранён
  // он только там, где `crypto.subtle` уже сработал.
  const match = await storage.matchesSavedPin(pincode);
  // debugPrint, а не dev.log: dev.log виден только в DevTools, а эту строку
  // нужно снимать с планшета через adb logcat. Только режим и флаги —
  // ни кода, ни хеша.
  // В браузере оба канала — одна консоль, но состав строки тот же.
  console.debug(
    `[PinOffline] mode=${type} network=${network} saved=${saved} match=${match}`,
  );

  // Журнал нужнее всего как раз без сети — пускаем по сохранённому коду.
  if (type === VerifyAdminType.diagnostics && network && match) {
    await proceed(context, type);
    return;
  }

  if (OFFLINE_DOORS.includes(type) && network) {
    if (match) {
      if (!mounted.current) return;
      SnackbarManager.instance.showSuccess({ title: l10n.offline_exit_by_saved_pin });
      await proceed(context, type);
      return;
    }

    // «Нет сети» здесь ничего не объясняет — сеть выключена намеренно.
    // Человеку нужно знать, почему не подошёл сохранённый код.
    showOfflineRefusal(context, failure, (messages) =>
      saved ? messages.offline_exit_pin_mismatch : messages.offline_exit_no_saved_pin,
    );
    return;
  }

  // Код верный, но выбран замок (смена аватара), а не дверь. Экран всегда
  // открывается с замком, и без этой подсказки выглядит так, будто
  // офлайн-выход не работает.
  if (type === VerifyAdminType.changePerson && network && match) {
    showOfflineRefusal(context, failure, (messages) => messages.offline_pin_only_for_exit);
    return;
  }

  controller.clear();
  SnackbarManager.instance.showFailure(failure);
}

/** `checkPincode` */
async function checkPincode(
  context: PinCheckContext,
  type: VerifyAdminType,
  pincode: number,
): Promise<void> {
  try {
    EasyLoading.dismiss();
    EasyLoading.show();
    const result = await appRepository.checkPincode(pincode);
    if (!result) return;

    // Код принят сервером — запоминаем, чтобы из киоска можно было выйти,
    // когда сети не будет.
    await StorageHelper.instance.savePinHash(pincode);

    if (!context.mounted.current) return;
    await proceed(context, type);
  } catch (e) {
    await handleFailure(context, type, pincode, e);
  } finally {
    EasyLoading.dismiss();
  }
}

/**
 * `mounted` из `State`: нужен там, где после `await` решается, уходить ли
 * с экрана.
 */
function useMounted(): RefObject<boolean> {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}

interface FooterActionsOptions {
  readonly selectedType: VerifyAdminType;
  readonly onSelect: (type: VerifyAdminType) => void;
  readonly onBack: () => void;
}

function buildFooterActions({
  selectedType,
  onSelect,
  onBack,
}: FooterActionsOptions): ReactNode[] {
  return [
    <PrimaryIconButtonRounded key="back" icon="arrowLeft" onPressed={onBack} />,
    ...SWITCHER_TYPES.map((type) => (
      <PrimaryIconButtonRounded
        key={type}
        isSelected={type === selectedType}
        icon={switcherIcon(type)}
        onPressed={() => onSelect(type)}
      />
    )),
    // Spacer(). За ним в Dart стоял отладочный просмотр папки, куда
    // DownloadUtil клал apk-release.apk; сам просмотр удалён вместе с
    // Android-частью, а Spacer в дереве был всегда.
    <div key="spacer" style={{ flex: 1 }} />,
  ];
}

export interface VerifyAdminPageProps {
  /**
   * Режим проверки. Не задан — берётся из адреса (`:type`), как это делал
   * `VerifyAdminRoute` в `routes.g.dart`.
   */
  readonly type?: VerifyAdminType;
}

export function VerifyAdminPage({ type }: VerifyAdminPageProps): ReactElement {
  const params = useParams();
  const l10n = useL10n();
  const navigate = useNavigate();
  const controller = useTextController();
  const mounted = useMounted();
  // `_verifyController = ValueNotifier(widget.type)`: начальное значение
  // берётся один раз, дальше им управляет переключатель в подвале.
  const [selectedType, setSelectedType] = useState<VerifyAdminType>(
    () => type ?? verifyAdminTypeFromParam(params.type),
  );

  const onComplete = (value: string): void => {
    const pin = tryParseInt(value);
    if (pin === null) return;
    void checkPincode({ controller, navigate, l10n, mounted }, selectedType, pin);
  };

  return (
    <BackgroundWrapper
      footerActions={buildFooterActions({
        selectedType,
        onSelect: setSelectedType,
        // `context.pop()` — родителем экрана PIN всегда был выбор языка.
        onBack: () => LanguageRoute.go(navigate),
      })}
    >
      <SafeArea className={styles.safeArea}>
        <div className={styles.center}>
          <div className={styles.scroll}>
            <div className={styles.column}>
              <PrimaryPinputField
                autoFocus
                title={l10n.enter_pincode}
                value={controller.value}
                onChange={controller.setValue}
                onComplete={onComplete}
              />
              <DigitKeyboard
                controller={controller}
                digitSize={wv(DIGIT_SIZE)}
                spacing={wv(DIGIT_SPACING)}
              />
            </div>
          </div>
        </div>
      </SafeArea>
    </BackgroundWrapper>
  );
}
